(function (upp) {
    'use strict';

    /**
     * Defines a new "private" property on the target, with the
     * name and value provided. "private" means not enumberable, nor
     * writable nor configurable.
     */
    function definePrivateProperty(target, name, value) {
        Object.defineProperty(target, name, {
            configurable: false,
            enumerable: false,
            writable: false,
            value: value,
        });
    }


    AppearanceApiService.$inject = [ '$http' ];
    function AppearanceApiService($http) {
        definePrivateProperty(this, '$http', $http);

    }

    /**
     * Returns the template information for the given template.
     * @param {number} templateId
     * @return {Promise<Template>}
     */
    AppearanceApiService.prototype.getTemplate = function(templateId) {
        return this.$http({
            method: 'GET',
            cache: true,
            url: $UPP.URL_UPPLICATION_REST_SERVICE + 'v2/templates/' + templateId,
        })
        .then(function(response) {
            return response.data;
        })
    }

    /**
     * Determines if the template identified by templateId supports the given featureName
     * @return Promise<boolean>
     */
    AppearanceApiService.prototype.isFeatureSupportedByTemplate = function(templateId, featureName) {
        return this.getTemplate(templateId)
        .then(function(template) {
            return !!template.features.find(function(feature) {
                return feature.name === featureName;
            });
        });
    }

    // Create a base Event name based on a runtime variable so no one can
    // actually listen to the event and force them to use the service instead.
    var EVENT_APPEARANCE_BASE = 'appearance_' + Date.now();

    var PROPERTY_TEMPLATE_ID = 'template_id'
    var FEATURE_FONT = 'font';
    var FEATURE_PRIMARY_TEXT_COLOR = 'primary_text_color';
    var FEATURE_SECONDARY_TEXT_COLOR = 'secondary_text_color';
    var FEATURE_HINT_TEXT_COLOR = 'hint_text_color';
    var FEATURE_SHOW_BOTTOM_NAVIGATION = 'show_bottom_navigation';


    /**
     * Provides setters and getters (as observables/subscriptions) for the childupp
     * appearance config which includes the following:
     * - font: Font family that the creator expects to be used to render texts on its app.
     * - primary_text_color: Color to be used when rendering important texts of the app.
     * - secondary_text_color: Color to be used when rendering medium-to-large text blocks of the app.
     * - hint_text_color: Color to be used when rendering hints or disabled texts on the app.
     * - show_bottom_navigation: Stringified boolean that determines if the bottom navigation should be shown.
     * @class AppearanceConfigService
     */
    AppearanceConfigService.$inject = [ '$timeout', '$q', '$rootScope', 'config', 'appearanceApiService' ];
    function AppearanceConfigService($timeout, $q, $rootScope, configService, appearanceApiService) {
        definePrivateProperty(this, '$timeout', $timeout);
        definePrivateProperty(this, '$q', $q);
        definePrivateProperty(this, '$rootScope', $rootScope);
        definePrivateProperty(this, 'configService', configService);
        definePrivateProperty(this, 'appearanceApiService', appearanceApiService);
        /**
         * Contains a mapping of feature names and its last known value.
         * @private
         * @property {object} AppearanceConfigService#featureValues
         */
        definePrivateProperty(this, 'featureValues', {})
        // Retrieve initial feature values.
        this._loadInitials()
    }

    /**
     * Returns a pseudo-private scoped event name based on the propertyName provided.
     * The pseudo-privateness consist on using a base event name based on the time
     * the service was initialized.
     * @private
     * @method AppearanceConfigService#_getEventName
     */
    AppearanceConfigService.prototype._getEventName = function(propertyName) {
        return [ EVENT_APPEARANCE_BASE, propertyName ].join('.')
    }

    /**
     * Sets the value for the given propertyName and notifies every subscriptor.
     * @protected
     * @method AppearanceConfigService#_setAppearanceValue
     * @param {string} propertyName - Name of the property which its value is being set
     * @param {string} value - new value for the property identified by propertyName
     */
    AppearanceConfigService.prototype._setAppearanceValue = function(propertyName, value) {
        this.featureValues[propertyName] = String(value)
        this.$rootScope.$emit(this._getEventName(propertyName), value)
    }

    /**
     * Registers a listener for it to be notified when changes on the property identified
     * by propertyName happen.
     * The given listener will recieve as first argument the property values always as a
     * string.
     * If values were notified to subscribers before the subscription, the last known
     * value will be notified asynchronusly to the just subscribed listener.
     * @protected
     * @method AppearanceConfigService#_onAppearanceConfigValueChange
     * @param {string} propertyName - Name of the property to listen for changes.
     * @param {function} listener - Callback to be invoked with new property values.
     * @return {function} unsubscribe function
     */
    AppearanceConfigService.prototype._onAppearanceConfigValueChange = function(propertyName, listener) {
        var lastKnownValue = this.featureValues[propertyName];
        if (lastKnownValue) {
            // If there was a value notified previously, provide it asynchronsly
            // right after the subscription
            this.$timeout(function() { listener(lastKnownValue) })
        }
        // Subscribe the listener to the value changes (which are handled via $rootScope emits)
        return this.$rootScope.$on(this._getEventName(propertyName), function(event, value) {
            listener(value)
        })
    }

    /**
     * Retrieve the remote features config, store them locally and notify every subscriber.
     * @private
     */
    AppearanceConfigService.prototype._loadInitials = function() {
        var _this = this;
        this.configService.getTemplateFeaturesConfig()
        .then(function(featureValuesConfig) {
            featureValuesConfig.forEach(function(featureValueConfig) {
                var featureName = featureValueConfig.featureName
                var value = featureValueConfig.value
                _this._setAppearanceValue(featureName, value)
            })
        })
    }

    /**
     * Sets the current templateId and notifies every subscriber listening for changes on it.
     */
    AppearanceConfigService.prototype.setTemplateId = function(templateId) {
        this._setAppearanceValue(PROPERTY_TEMPLATE_ID, templateId);
    }

    /**
     * Registers the listener for changes on the templateId value.
     * If values were notified to subscribers before this subscription, the last known
     * value will be notified asynchronusly to the just subscribed listener.
     * @return {function} unsubscribe function
     */
    AppearanceConfigService.prototype.onTemplateIdChange = function(listener) {
        return this._onAppearanceConfigValueChange(PROPERTY_TEMPLATE_ID, listener);
    }

    /**
     * Sets the current font and notifies every subscriber listening for changes on it.
     */
    AppearanceConfigService.prototype.setFont = function(value) {
        this._setAppearanceValue(FEATURE_FONT, value);
    }

    /**
     * Registers the listener for changes on the font feature value.
     * If values were notified to subscribers before this subscription, the last known
     * value will be notified asynchronusly to the just subscribed listener.
     * @return {function} unsubscribe function
     */
    AppearanceConfigService.prototype.onFontChange = function(listener) {
        return this._onAppearanceConfigValueChange(FEATURE_FONT, listener);
    }

    /**
     * Sets the current primary text color and notifies every subscriber listening for changes on it.
     */
    AppearanceConfigService.prototype.setPrimaryTextColor = function(value) {
        this._setAppearanceValue(FEATURE_PRIMARY_TEXT_COLOR, value);
    }

    /**
     * Registers the listener for changes on the primary text color feature value.
     * If values were notified to subscribers before this subscription, the last known
     * value will be notified asynchronusly to the just subscribed listener.
     * @return {function} unsubscribe function
     */
    AppearanceConfigService.prototype.onPrimaryTextColorChange = function(listener) {
        return this._onAppearanceConfigValueChange(FEATURE_PRIMARY_TEXT_COLOR, listener);
    }

    /**
     * Sets the current secondary text color and notifies every subscriber listening for changes on it.
     */
    AppearanceConfigService.prototype.setSecondaryTextColor = function(value) {
        this._setAppearanceValue(FEATURE_SECONDARY_TEXT_COLOR, value);
    }

    /**
     * Registers the listener for changes on the secondary text color feature value.
     * If values were notified to subscribers before this subscription, the last known
     * value will be notified asynchronusly to the just subscribed listener.
     * @return {function} unsubscribe function
     */
    AppearanceConfigService.prototype.onSecondaryTextColorChange = function(listener) {
        return this._onAppearanceConfigValueChange(FEATURE_SECONDARY_TEXT_COLOR, listener);
    }

    /**
     * Sets the current hint text color and notifies every subscriber listening for changes on it.
     */
    AppearanceConfigService.prototype.setHintTextColor = function(value) {
        this._setAppearanceValue(FEATURE_HINT_TEXT_COLOR, value);
    }

    /**
     * Registers the listener for changes on the hint text color feature value.
     * If values were notified to subscribers before this subscription, the last known
     * value will be notified asynchronusly to the just subscribed listener.
     * @return {function} unsubscribe function
     */
    AppearanceConfigService.prototype.onHintTextColorChange = function(listener) {
        return this._onAppearanceConfigValueChange(FEATURE_HINT_TEXT_COLOR, listener);
    }

    /**
     * Sets the current show bottom navigation value and notifies every subscriber listening for changes on it.
     */
    AppearanceConfigService.prototype.setShowBottomNavigation = function(value) {
        this._setAppearanceValue(FEATURE_SHOW_BOTTOM_NAVIGATION, value);
    }

    /**
     * Registers the listener for changes on the show bottom navigation feature value.
     * If values were notified to subscribers before this subscription, the last known
     * value will be notified asynchronusly to the just subscribed listener.
     * @return {function} unsubscribe function
     */
    AppearanceConfigService.prototype.onShowBottomNavigationChange = function(listener) {
        return this._onAppearanceConfigValueChange(FEATURE_SHOW_BOTTOM_NAVIGATION, listener);
    }

    /**
     * Passive service that listens for changes on font via AppearanceConfigService
     * and takes responsability of loading the stylesheets necessary for said fonts
     * to render properly on the document
     */
    FontLoaderService.$inject = ['$document', 'appearanceConfigService']
    function FontLoaderService($document, appearanceConfigService) {
        definePrivateProperty(this, '$head', $document.find('head'));
        appearanceConfigService.onFontChange(function(fontName) {
            if (!this.isLoaded(fontName))
                this.loadFont(fontName)
        }.bind(this));
    }

    /**
     * Determines if the given font has been loaded into the available fonts on
     * the document.
     */
    FontLoaderService.prototype.isLoaded = function(fontName) {
        var fontStylesheet = this.$head.find('link[href*=\"' + fontName + '\"]');
        return fontStylesheet.length > 0;
    }

    /**
     * Loads the stylesheet for the given fontName into the available font names
     * of the document.
     */
    FontLoaderService.prototype.loadFont = function(fontName) {
        var typeWeights = [ '400', '400i', '700', '700i' ];
        var fontRes = fontName + ':' + typeWeights.join(',')
        var linkElem = angular.element('<link/>');
        linkElem.attr('rel', 'stylesheet');
        linkElem.attr('href', 'https://fonts.googleapis.com/css?family=' + fontRes);
        this.$head.append(linkElem);
    }

    upp
    .service('appearanceApiService', AppearanceApiService)
    .service('appearanceConfigService', AppearanceConfigService)
    .service('fontLoaderService', FontLoaderService)
})(upp);


// WEBPACK FOOTER //
// ./src/app/common/services/appearance-config-service.js